import { ConsultationsService } from "@publicPortal/services/ConsultationsService";
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Comments from "@publicPortal/components/comments/Comments";
import type { ICommentCard } from "@publicPortal/components/comments/commentCard/CommentCard";
import { Tab, Tabs, TextField } from "@mui/material";
import "../PlanningApplication.css";
import { GetPublicConsulteeCommentsResponse, GetUserConsulteeCommentsResponse, IConsulteeCommentToSave } from "@shared/publicportal/contracts/ConsulteeComments";
import CommentOpinionSelector, { OpinionTypes, OPINIONS } from "./components/CommentOpinionSelector";
import InputErrorMessage from "@mirage/components/inputErrorMessage/InputErrorMessage";
import { CommentsTabProps } from "./PACommentsTabContent";
import IsAnonymousRadioButtons from "./components/IsAnonymousRadioButtons";
import NoCommentsMessage from "./components/NoCommentsMessage";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import NewCommentAttachments, { commentFiles } from "./components/NewCommentAttachments";
import { useRecoilValue } from "recoil";
import { UploadedFile } from "@shared/mirage/core/contracts/UploadedFile";
import useAlertService from "@mirage/hooks/useAlertService";
import { VerifyConsulteeResult } from "@shared/mirage/core/contracts/User";
import { PublicPortalEntities } from "@shared/mirage/contracts/PublicPortalEntities";
import UserCustomProperty from "@shared/mirage/utils/getUserCustomProperty";
import NewComment from "@publicPortal/components/comments/newComment/NewComment";

const ConsultationsTabTypes = {
    USER_COMMENTS: "user-consultations",
    PUBLIC_COMMENTS: "public-consultations",
} as const;
type ConsultationsTabTypes = typeof ConsultationsTabTypes[keyof typeof ConsultationsTabTypes];

const PAConsultationsTabContent: React.FC<CommentsTabProps> = ({ paId, newCommentsDisabled, user }) => {
    const consultationsService = new ConsultationsService();
    const [commentOpinion, setCommentOpinion] = useState<OpinionTypes>(OPINIONS.consultee.Neutral);
    const [publicComments, setPublicComments] = useState<GetPublicConsulteeCommentsResponse>({} as GetPublicConsulteeCommentsResponse);
    const [userComments, setUserComments] = useState<GetUserConsulteeCommentsResponse>({} as GetUserConsulteeCommentsResponse);
    const [remainAnonymous, setRemainAnonymous] = useState("no");
    const [newCommentContent, setNewCommentContent] = useState({ value: "", error: "" });
    const [currentTab, setCurrentTab] = useState<ConsultationsTabTypes>(ConsultationsTabTypes.PUBLIC_COMMENTS);
    const commentAttachments = useRecoilValue<UploadedFile[]>(commentFiles);
    const showAlert = useAlertService();
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const consulteeTokenString = localStorage.getItem("verifyConsultee.token");
    const consulteeToken: Omit<VerifyConsulteeResult, "consulteeEmail" | "consulteePassword"> = consulteeTokenString && JSON.parse(consulteeTokenString);

    useEffect(() => {
        getPublicConsulteeComments();
        getUserConsulteeComments();
    }, []);

    return (
        <Box className="comments__comments-container">
            <Tabs
                className="comments__comments-tabs"
                aria-label="Consultations Navigation Tabs"
                variant="scrollable"
                scrollButtons="auto"
                value={currentTab}
                onChange={(event, newTab: ConsultationsTabTypes) => setCurrentTab(newTab)}
            >
                <Tab
                    disabled={!user?.id}
                    className="comments__comments-tabs_sub-tab"
                    key={"my-consultations"}
                    disableRipple
                    label={"My Consultations"}
                    value={ConsultationsTabTypes.USER_COMMENTS}
                />
                <Tab
                    className="comments__comments-tabs_sub-tab"
                    key={"public-consultations"}
                    disableRipple
                    label={"Public Consultations"}
                    value={ConsultationsTabTypes.PUBLIC_COMMENTS}
                />
            </Tabs>
            {getTabContent()}
        </Box>
    );

    function getTabContent() {
        if (currentTab === "user-consultations") {
            return renderMyConsultations();
        }
        return renderPublicConsultations();
    }

    function renderMyConsultations() {
        if (consulteeId) {
            return (
                <>
                    {!newCommentsDisabled ? getNewCommentContent() : <NoCommentsMessage message="This application is closed for further comments." variant="disabled" />}
                    <Comments className="comments__my-comments" records={getUserConsultationCommentCards()} />
                </>
            );
        }
        return <NoCommentsMessage message="Only consultee user can add consultations." variant="disabled" />;
    }

    function renderPublicConsultations() {
        if (publicComments?.comments?.length) {
            return <Comments records={getConsultationCommentCards()} />;
        }
        return <NoCommentsMessage message="There are no consultations yet." variant="empty" />;
    }

    function getNewCommentContent() {
        return (
            <NewComment handleSave={async () => await handleNewCommentSave()}>
                <CommentOpinionSelector type="consultee" opinion={commentOpinion} changeOpinion={(newOpinion) => setCommentOpinion(newOpinion)} />
                <IsAnonymousRadioButtons remainAnonymous={remainAnonymous} remainAnonymousOnChange={handleRemainAnonymousOnChange} />
                <TextField
                    error={!!newCommentContent.error}
                    helperText={<InputErrorMessage passed={!newCommentContent.error} message={newCommentContent.error} />}
                    multiline
                    minRows={4}
                    label="Your Comment"
                    value={newCommentContent.value}
                    onChange={(e) => handleContentChange(e.target.value)}
                />
                <NewCommentAttachments />
            </NewComment>
        );
    }

    function handleContentChange(value: string) {
        setNewCommentContent(() => {
            return {
                error: "",
                value: value,
            };
        });
    }

    async function handleNewCommentSave() {
        const isValid = validateNewConsultation();
        if (!isValid) {
            return true;
        }
        const newConsultation = getNewConsultationBody();
        const createNewConsultationResponse = await consultationsService.create({ comment: newConsultation, attachments: commentAttachments });
        if (createNewConsultationResponse.errorMsg) {
            showAlert(createNewConsultationResponse.errorMsg, "error");
            return true;
        } else {
            await getUserConsulteeComments();
            setNewCommentDefaultValues();
            return false;
        }
    }

    function validateNewConsultation() {
        if (!newCommentContent.value) {
            setNewCommentContent((prev) => {
                return { ...prev, error: "Enter your consultation" };
            });
            return false;
        } else if (!consulteeToken.consultationId) {
            throw Error("validateNewConsultation - No consultation record found.");
        }
        return true;
    }

    function getNewConsultationBody(): IConsulteeCommentToSave {
        return {
            consultationId: consulteeToken.consultationId,
            responseContent: newCommentContent.value,
            opinion: commentOpinion,
            remainAnonymous: remainAnonymous === "no" ? 0 : 1,
            paId: paId,
        } as IConsulteeCommentToSave;
    }

    function handleRemainAnonymousOnChange(event: React.ChangeEvent<HTMLInputElement>) {
        setRemainAnonymous(event.target.value);
    }

    function getUserConsultationCommentCards(): ICommentCard[] {
        return userComments?.comments?.map((comment, index) => {
            const commentCard: ICommentCard = {
                cardContent: comment.responseContent,
                cardTitle: `${comment.organisation || "Anonym"} `,
                commentKey: `${comment.id}-${comment.createdTime}`,
                subheader: `${DateFormatter.formatToDateTime(comment.createdTime)} ${comment.opinion || ""}`,
                attachments: {
                    commentId: comment.id,
                    entityName: PublicPortalEntities.P_CONSULTEE_COMMENT,
                    attachmentsCounter: userComments.attachments[index]?.length,
                },
            };
            return commentCard;
        });
    }

    function getConsultationCommentCards(): ICommentCard[] {
        return publicComments.comments?.map((comment, index) => {
            const commentCard: ICommentCard = {
                cardContent: comment.responseContent,
                cardTitle: `${comment.organisation || "Anonym"} `,
                commentKey: `${comment.id}-${comment.createdTime}`,
                subheader: `${DateFormatter.formatToDateTime(comment.createdTime)} ${comment.opinion || ""}`,
                attachments: {
                    commentId: comment.id,
                    entityName: PublicPortalEntities.P_CONSULTEE_RESPONSE,
                    attachmentsCounter: publicComments.attachments[index]?.length,
                },
            };
            return commentCard;
        });
    }

    function setNewCommentDefaultValues() {
        setCommentOpinion("Neutral");
        setRemainAnonymous("no");
        setNewCommentContent({ value: "", error: "" });
    }

    async function getPublicConsulteeComments() {
        const response = await consultationsService.getPublicConsultations(paId);
        setPublicComments(response);
    }

    async function getUserConsulteeComments() {
        if (!consulteeId) {
            return;
        }
        const response = await consultationsService.getUserConsultations({ paId });
        setUserComments(response);
    }
};

export default PAConsultationsTabContent;
